import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      fetchUser();
    } else {
      setLoading(false);
    }
  }, [token]);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      console.error('Error fetching user:', error);
      logout();
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    if (token) {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
      } catch (error) {
        console.error('Error refreshing user:', error);
      }
    }
  };

  const login = async (email, password) => {
    const response = await axios.post(`${API}/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (userData) => {
    const response = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user: userResponse } = response.data;
    localStorage.setItem('token', access_token);
    axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
    setToken(access_token);
    setUser(userResponse);
    return userResponse;
  };

  const logout = async () => {
    try {
      // Try to logout on server (clear session)
      if (token) {
        await axios.post(`${API}/auth/logout`);
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
      setToken(null);
      setUser(null);
    }
  };

  const isPremium = () => {
    if (!user) return false;
    
    // Check new dual membership fields first
    if (user.attendee_membership === 'attendee_premium' && user.attendee_membership_expires) {
      if (new Date(user.attendee_membership_expires) > new Date()) return true;
    }
    if (user.business_membership === 'business_premium' && user.business_membership_expires) {
      if (new Date(user.business_membership_expires) > new Date()) return true;
    }
    
    // Fall back to legacy field
    if (user.membership_tier === 'free') return false;
    if (!user.membership_expires) return false;
    return new Date(user.membership_expires) > new Date();
  };

  const isBusinessPremium = () => {
    if (!user) return false;
    
    // Check new dual membership field first
    if (user.business_membership === 'business_premium' && user.business_membership_expires) {
      return new Date(user.business_membership_expires) > new Date();
    }
    
    // Fall back to legacy
    return isPremium() && user?.membership_tier === 'business_premium';
  };

  const isAttendeePremium = () => {
    if (!user) return false;
    
    // Check new dual membership field first
    if (user.attendee_membership === 'attendee_premium' && user.attendee_membership_expires) {
      return new Date(user.attendee_membership_expires) > new Date();
    }
    
    // Fall back to legacy
    return isPremium() && user?.membership_tier === 'attendee_premium';
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      setUser,
      token, 
      setToken,
      login, 
      register, 
      logout, 
      loading, 
      refreshUser,
      isPremium,
      isBusinessPremium,
      isAttendeePremium,
      isAdmin
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
